import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FullWidthContainer, PageLoader, Modal } from '../../components';
import { useCallRoutes } from './hooks';
import { CallRoutesTable, CreateRouteModal, EditRouteModal } from './components';
import { CreateRouteData, CallRoute, UpdateRouteData } from './types';
import { useToast } from '../../contexts/useToast';
import { useMinimumLoader } from '../../hooks/useMinimumLoader';

const CallRouting: React.FC = () => {
  const { t } = useTranslation();
  const { callRoutes, loading, error, createCallRoute, updateCallRoute, deleteCallRoute } =
    useCallRoutes();
  const { showToast } = useToast();

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    route: CallRoute | null;
  }>({ isOpen: false, route: null });
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    route: CallRoute | null;
  }>({ isOpen: false, route: null });

  const showLoader = useMinimumLoader(loading, { minimumDuration: 500 });

  const handleCreateRoute = async (data: CreateRouteData) => {
    const result = await createCallRoute(data);
    if (result.success) {
      showToast({
        type: 'success',
        title: t('callRouting.callRouteCreated'),
        message: t('callRouting.callRouteCreatedMessage', { name: data.name }),
        duration: 5000,
      });
      setIsCreateModalOpen(false);
      return undefined;
    } else {
      if (result.errors?.generalError) {
        showToast({
          type: 'error',
          title: t('callRouting.failedToCreateCallRoute'),
          message: result.errors.generalError,
          duration: 5000,
        });
      }
      return result.errors;
    }
  };

  const handleDeleteRoute = (route: CallRoute) => {
    setDeleteModal({ isOpen: true, route });
  };

  const confirmDelete = async () => {
    if (deleteModal.route) {
      const success = await deleteCallRoute(deleteModal.route.id);
      if (success) {
        showToast({
          type: 'success',
          title: t('callRouting.callRouteDeleted'),
          message: t('callRouting.callRouteDeletedMessage', { name: deleteModal.route.name }),
          duration: 5000,
        });
      }
      setDeleteModal({ isOpen: false, route: null });
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, route: null });
  };

  const handleEditRoute = (id: string) => {
    const route = callRoutes.find(r => r.id === id);
    if (route) {
      setEditModal({ isOpen: true, route });
    }
  };

  const handleUpdateRoute = async (data: UpdateRouteData) => {
    if (!editModal.route) return undefined;

    const result = await updateCallRoute(editModal.route.id, data);
    if (result.success) {
      showToast({
        type: 'success',
        title: t('callRouting.callRouteUpdated'),
        message: t('callRouting.callRouteUpdatedMessage', { name: data.name }),
        duration: 5000,
      });
      setEditModal({ isOpen: false, route: null });
      return undefined;
    } else {
      if (result.errors?.generalError) {
        showToast({
          type: 'error',
          title: t('callRouting.failedToUpdateCallRoute'),
          message: result.errors.generalError,
          duration: 5000,
        });
      }
      return result.errors;
    }
  };

  const cancelEdit = () => {
    setEditModal({ isOpen: false, route: null });
  };

  if (showLoader) {
    return <PageLoader show={true} />;
  }

  if (error) {
    return (
      <FullWidthContainer>
        <div className="space-y-6">
          <div className="text-center py-8">
            <p className="text-red-500 text-lg font-semibold">Error loading call routing</p>
            <p className="text-sm text-gray-500 mt-2">{error}</p>
          </div>
        </div>
      </FullWidthContainer>
    );
  }

  return (
    <FullWidthContainer>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {t('callRouting.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">{t('callRouting.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="gradient-multi"
              onClick={() => setIsCreateModalOpen(true)}
              className="flex items-center gap-2"
            >
              <span className="material-icons">add</span>
              {t('callRouting.addRoute')}
            </Button>
          </div>
        </div>

        {/* Call Routes Table */}
        <CallRoutesTable
          routes={callRoutes}
          onEdit={handleEditRoute}
          onDelete={(id: string) => {
            const route = callRoutes.find(r => r.id === id);
            if (route) handleDeleteRoute(route);
          }}
          onCreateRoute={() => setIsCreateModalOpen(true)}
        />

        {/* Create Route Modal */}
        <CreateRouteModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateRoute}
        />

        {/* Edit Route Modal */}
        <EditRouteModal
          isOpen={editModal.isOpen}
          onClose={cancelEdit}
          onSubmit={handleUpdateRoute}
          route={editModal.route}
        />

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteModal.isOpen}
          onClose={cancelDelete}
          title=""
          size="sm"
          showCloseButton={false}
        >
          <div className="text-center space-y-6">
            {/* Warning Icon */}
            <div className="mx-auto w-16 h-16 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <span className="material-icons text-3xl text-red-600 dark:text-red-400">
                delete_forever
              </span>
            </div>

            {/* Content */}
            <div className="space-y-3">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('callRouting.deleteCallRoute')}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {t('callRouting.areYouSureDelete')}{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  &ldquo;{deleteModal.route?.name}&rdquo;
                </span>
                ?
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-500">
                {t('callRouting.thisActionCannotBeUndone')}
              </p>
            </div>

            {/* Route Info Card */}
            {deleteModal.route && (
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/20 rounded-full flex items-center justify-center">
                    <span className="material-icons text-primary-600 dark:text-primary-400 text-sm">
                      route
                    </span>
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 dark:text-white">
                      {deleteModal.route.name}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {deleteModal.route.phone_number} • {deleteModal.route.business_hours}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                onClick={cancelDelete}
                className="flex-1 px-6 py-3 font-medium"
              >
                {t('callRouting.cancel')}
              </Button>
              <Button
                variant="danger"
                onClick={confirmDelete}
                className="flex-1 px-6 py-3 font-medium"
              >
                <span className="material-icons text-sm mr-2">delete</span>
                {t('callRouting.deleteRoute')}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </FullWidthContainer>
  );
};

export { CallRouting };
